// CompreJunto.js — Seção "Compre junto": livro atual + uma sugestão = total, com
// botão que adiciona ambos ao carrinho.
//
// ================= A SUGESTÃO É SEMPRE DA MESMA ÁREA (17/09/2026) =================
//
// Ela vinha de `popular-ebooks`: o mais vendido da editora, fosse qual fosse o
// assunto. O print da página de um livro de ECONOMIA E ENERGIA oferecia, para
// levar junto, um manual de metodologia científica — "compre junto" com um
// livro que nada tinha a ver com o que a pessoa estava olhando. O dono pediu
// que o par fosse sempre da mesma categoria.
//
// A escolha, em ordem de desempate:
//   1. MAIS ÁREAS EM COMUM. Um livro de "Economia" e "Energia" combina mais com
//      outro de "Economia" e "Energia" do que com um que só divide "Economia".
//   2. MAIS VENDIDO, entre os empatados — `vendas` vem no catálogo.
//   3. MAIS RECENTE (id maior), para o empate final não depender da ordem da API.
//
// SEM NENHUM DA MESMA ÁREA, A SEÇÃO SOME. Cair para "o mais vendido" seria voltar
// exatamente ao par sem sentido que motivou a mudança.
//
// A lista é o catálogo público (`/ebook`), que já traz categorias, vendas e o
// preço com desconto calculado pelo servidor — sem endpoint novo.
import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CarrinhoContext } from "../../../services/context/CarrinhoContext";
import { useModal } from "../../../services/context/ModalContext";
import NotaLivro from "./NotaLivro";
import { camposDePreco, precoDeVenda } from "../../../utils/precoLivro";
import PrecoComDesconto from "../../ui/PrecoComDesconto";
import Vitrine from "../vitrine/Vitrine";

const API_URL = process.env.REACT_APP_API;
const main = process.env.REACT_APP_MAIN;

const fmt = (n) => (Number(n) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const precoValido = (p) => p && !isNaN(parseFloat(p)) && parseFloat(p) > 0;

/** Nomes de categoria do livro, aceitando string ou { nome }. */
const areasDe = (livro) =>
  new Set(
    (Array.isArray(livro?.categorias) ? livro.categorias : [])
      .map((c) => (typeof c === "string" ? c : c?.nome))
      .filter(Boolean)
  );

/**
 * O melhor par da mesma área para `atual`, ou null.
 *
 * FICA DE FORA: o próprio livro, livro sem preço digital (o combo vende os dois
 * como e-book) e livro com cópia digital grátis — não há o que pôr no carrinho.
 */
export const melhorParDaMesmaArea = (atual, catalogo) => {
  const minhas = areasDe(atual);
  if (minhas.size === 0) return null;

  let melhor = null;
  (Array.isArray(catalogo) ? catalogo : []).forEach((livro) => {
    if (!livro || String(livro.id) === String(atual.id)) return;
    if (!precoValido(livro.preco) || livro.copia_gratis_ativa === true) return;

    const emComum = [...areasDe(livro)].filter((a) => minhas.has(a));
    if (emComum.length === 0) return;

    const pontos = [emComum.length, Number(livro.vendas) || 0, Number(livro.id) || 0];
    const ganha =
      !melhor ||
      pontos[0] > melhor.pontos[0] ||
      (pontos[0] === melhor.pontos[0] &&
        (pontos[1] > melhor.pontos[1] ||
          (pontos[1] === melhor.pontos[1] && pontos[2] > melhor.pontos[2])));
    if (ganha) melhor = { livro, pontos, area: emComum[0] };
  });

  return melhor ? { livro: melhor.livro, area: melhor.area } : null;
};

const autorNome = (livro) => {
  const a = livro?.autores;
  if (Array.isArray(a) && a.length) return typeof a[0] === "object" ? a[0].nome : a[0];
  return livro?.autor || "";
};

// A LARGURA ENCOLHE NO CELULAR (17/09/2026): dois livros de 160px, o "+" e os
// vãos somavam ~390px numa coluna de 358px, e o par quebrava em dois — capa,
// "+", outra capa e "=" empilhados, ~1.000px para um bloco que diz "leve os
// dois". Com 9rem o par cabe numa fileira só.
const LivroMini = ({ livro }) => (
  <div className="w-[9rem] sm:w-40">
    <Link to={`${main}livros/${livro.id}`} onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="block">
      {/* A MESMA MOLDURA DA CAPA DO RESTO DO SITE: raio de card, borda de 2px
          na cor da sombra e o poço navy-deep atrás da imagem — é como
          CardDestaque, CardDoClube e o hero desta página emolduram uma capa.
          Aqui a capa vinha só com `rounded-lg` e sem moldura, e as duas capas
          desta seção flutuavam soltas ao lado do "+" e do "=". */}
      <div className="rounded-card overflow-hidden border-2 border-insigne-sombra bg-insigne-navy-deep aspect-[3/4] flex items-center justify-center">
        {livro.image ? <img src={`${API_URL}public/${livro.image}`} alt={livro.titulo} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} /> : <i className="bi bi-book text-white/30 text-2xl" />}
      </div>
      <h4 className="mt-2 font-bold text-sm leading-tight line-clamp-2 min-h-[2.5rem]">{livro.titulo}</h4>
    </Link>
    {autorNome(livro) && <p className="text-white/50 text-xs mt-0.5 line-clamp-1">{autorNome(livro)}</p>}
    {/* Mini-estrelas: aqui a pessoa está decidindo levar um SEGUNDO livro que
        não veio procurar — a nota é o único dado que ajuda nessa decisão. */}
    <NotaLivro livro={livro} size="text-[11px]" compacto className="mt-1" />
    {/* O preço que se COBRA, no desenho de toda vitrine. Aqui se lia
        `livro.preco` — o cheio — e o total ao lado já somava os preços com
        desconto: a tela dizia "R$ 30,00 + R$ 30,00 = R$ 54,00". A linha do
        riscado fica reservada para os dois livros terem o preço na mesma
        altura quando só um deles está em promoção. */}
    <PrecoComDesconto livro={livro} numero="text-xl" className="mt-1" />
  </div>
);

const CompreJunto = ({ ebook }) => {
  const { adicionarItem } = useContext(CarrinhoContext);
  const { openModal } = useModal();
  const [sugestao, setSugestao] = useState(null);
  // A área que os dois livros dividem, para a frase da seção dizer QUAL é.
  const [areaEmComum, setAreaEmComum] = useState("");

  useEffect(() => {
    let ativo = true;
    // Trocar de livro zera a sugestão antes da busca: sem isto, a página do
    // livro seguinte mostraria por um instante o par do livro anterior.
    setSugestao(null);
    setAreaEmComum("");
    const buscar = async () => {
      try {
        const resp = await fetch(`${API_URL}ebook`);
        const data = resp.ok ? await resp.json() : [];
        const par = melhorParDaMesmaArea(ebook, data);
        if (ativo && par) {
          setSugestao(par.livro);
          setAreaEmComum(par.area);
        }
      } catch { /* sem sugestão → seção não aparece */ }
    };
    buscar();
    return () => { ativo = false; };
    // `ebook.categorias` entra junto do id: é dela que a escolha depende.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ebook.id, JSON.stringify(ebook.categorias || [])]);

  // Livro com cópia digital grátis não entra em combo: o combo vende os dois
  // como e-book, e este e-book não se vende — se lê de graça.
  if (!sugestao || !precoValido(ebook.preco) || ebook.copia_gratis_ativa === true) return null;

  // O TOTAL TEM DE SER O QUE VAI PARA O CARRINHO.
  //
  // Aqui somava-se `ebook.preco` + `sugestao.preco` -- os precos CHEIOS --
  // enquanto `toItem` logo abaixo manda o preco COM desconto. Com dois livros de
  // R$ 39,90 em destaque, o bloco anunciava "Total: R$ 79,80" e o carrinho
  // recebia R$ 71,82. Numero diferente em duas telas seguidas nao parece
  // promocao: parece erro.
  const total = (precoDeVenda(ebook) ?? 0) + (precoDeVenda(sugestao) ?? 0);
  // A nota e o autor acompanham o item: é o que permite ao carrinho mostrar as
  // estrelas de cada linha sem uma requisição por item.
  const toItem = (l) => ({
      // O preco que vai para o carrinho e o do SERVIDOR (precoDeVenda le
      // `preco_com_desconto`). Mandar o cheio de um livro em destaque faz o
      // checkout recusar o pedido inteiro -- ver src/utils/precoLivro.js.
    id: l.id, titulo: l.titulo, image: l.image, autores: l.autores,
    ...camposDePreco(l), formato: "ebook", quantidade: 1,
    avaliacoes_avg_nota: l.avaliacoes_avg_nota,
    avaliacoes_count: l.avaliacoes_count,
  });

  const adicionarAmbos = () => {
    adicionarItem(toItem(ebook));
    adicionarItem(toItem(sugestao));
    openModal(toItem(ebook));
  };

  return (
    // `padding=""`: esta seção mora DENTRO da página do produto, que já traz o
    // respiro lateral — ver o mesmo comentário em LivrosRelacionados.js.
    <Vitrine
      chapeu="Leve os dois"
      titulo="Compre junto"
      icone="bi-bag-plus"
      // A frase diz de onde a sugestão vem, e nomeia a área. "Comprados
      // juntos" seria falso: não existe histórico de compra casada neste site.
      descricao={`Outro título de ${areaEmComum}, para levar junto com este num clique só.`}
      padding=""
      className="mb-12"
    >
      {/* DUAS FILEIRAS NO CELULAR, UMA NO COMPUTADOR. O par (livro + livro)
          é uma peça que nunca se separa; o total com o botão desce inteiro
          para baixo dele quando não cabe ao lado. O "=" só aparece quando o
          total está ao lado — embaixo, ele apontaria para o vazio.
          Vãos com meio passo (`gap-2.5`, `gap-x-6`): `gap-4` é homônimo do
          Bootstrap e vira 1,5rem com !important. */}
      <div className="flex flex-col items-center gap-6 lg:flex-row lg:items-start lg:justify-start lg:gap-x-6">
        <div className="flex items-start justify-center gap-2.5 sm:gap-x-6">
          <LivroMini livro={ebook} />
          {/* self-stretch dá ao "+" a altura da fileira, e items-center o põe na
              altura do meio da capa. */}
          <div className="self-stretch flex items-center">
            <span className="text-3xl font-light text-white/50">+</span>
          </div>
          <LivroMini livro={sugestao} />
        </div>
        <div className="hidden self-stretch items-center lg:flex">
          <span className="text-3xl font-light text-white/50">=</span>
        </div>
        <div className="flex w-full flex-col justify-center gap-2.5 self-stretch sm:w-auto sm:min-w-[260px] lg:min-w-[200px]">
          <p className="max-lg:text-center text-white/80">Total: <span className="text-3xl font-extrabold text-white"><span className="text-base align-top">R$ </span>{fmt(total)}</span></p>
          {/* `btn-solido` (App.css) é a peça do botão laranja cheio: fundo,
              borda, sombra e a inversão no hover vêm de lá. O que estava aqui
              era a receita refeita à mão — inclusive um `shadow-lg`, que é do
              Bootstrap e vem com !important, ou seja: a sombra desta tela não
              era a sombra do sistema. */}
          <button onClick={adicionarAmbos} className="btn-solido foco-laranja inline-flex items-center justify-center gap-2 rounded-controle px-6 py-3.5 font-bold uppercase text-sm">
            <i className="bi bi-cart-plus" /> Adicionar ao Carrinho
          </button>
        </div>
      </div>
    </Vitrine>
  );
};

export default CompreJunto;
